import { ApiError, type Api } from "../api/client.js";
import type { AccountView, HistoryRange, HistoryRead, LedgerEntry, MandateView } from "../api/types.js";
import type { AimHandlers } from "../ui/aim.js";
import { InvestView, holdingSheetView } from "../ui/invest.js";
import { openSheet, type SheetHandle } from "../ui/sheet.js";
import type { Tab } from "../ui/tabs.js";
import { filledLines, holdingSheet, investModel, sheetChartOf, type FilledLine, type HistoryState, type InvestReads, type OrdersRead } from "./invest-model.js";
import { receiptModel, type BundleRead, type ReceiptContext } from "./receipt-model.js";
import { ReceiptSheets } from "./receipt-sheets.js";

/**
 * The Invest tab's controller (DCTA-229; spec 10-invest §4, §6): the reads, the model, the range, the layers.
 *
 * Reads (§4.1, §4.2): the valuation and the portfolio first — without them there is no tab (the banner and Retry); a
 * customer with no trading account and nothing held is asked nothing more. Then, side by side, what is waiting, the
 * schedules into the trading account, the accounts (a lock), the history for the session's range, the orders. Each optional
 * read falls back on a 404 (§4.3): no history → the valuation's day change, no chart; no orders read → the trading account's
 * ledger lines and their receipts. A read that fails beside them is left out, never shown as "none".
 *
 * The range is the session's (in memory, §6.2); a range's answer that arrives after a newer press is dropped. The holding
 * sheet reads its own instrument's history (read B). Orders and held buys open the bank's receipt over the tab — the same
 * receipt sheets as Activity's, Cancel included (06 from a receipt). Nothing here signs or trades.
 */
export interface InvestDeps {
  readonly api: Api;
  readonly host: HTMLElement;
  /** The bank's now: "today" is the bank's. */
  readonly now: () => number;
  readonly reduced: () => boolean;
  readonly judgeOn: () => boolean;
  readonly onProof: (bundleId: string) => void;
  readonly onTab: (tab: Tab) => void;
  readonly assistant: AimHandlers;
  readonly onSignedOut: () => void;
  /** The identity check has lapsed where its refresh exists (Home's rule). */
  readonly lapsed: () => boolean;
  readonly onUpdateKyc: () => void;
  /** Money locked: Unlock is Me › Security (19). */
  readonly onUnlock: () => void;
  readonly onSchedule: (mandateId: string) => void;
  readonly onSeeAllOrders: () => void;
}

/** What the bank said, kept between draws; the live values (the bank's now, the identity check) join it at each draw. */
type BankReads = Omit<InvestReads, "lapsed" | "nowMs">;

const auth = (error: unknown): boolean => error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED";

/** A side read that fails is left out (undefined) — unless the session ended, which ends the whole read. */
function rethrowAuth(error: unknown): undefined {
  if (auth(error)) throw error;
  return undefined;
}

/** A history read (A or B) as the chart takes it: the bank's points, its 404 (no such read), or a failure (Retry). */
async function historyState(read: Promise<HistoryRead | undefined>): Promise<HistoryState> {
  try {
    const r = await read;
    return r === undefined ? { kind: "absent" } : { kind: "ok", read: r };
  } catch (error) {
    if (auth(error)) throw error;
    return { kind: "failed" };
  }
}

export class InvestScreen {
  readonly view: InvestView;
  readonly #d: InvestDeps;
  readonly #receipts: ReceiptSheets;
  #range: HistoryRange = "1m";
  /** The bank answered 404 to read C this session: it is not asked again (the ledger answers). */
  #ordersAbsent = false;
  #reads: BankReads | undefined;
  #accounts: readonly AccountView[] = [];
  #entries: readonly LedgerEntry[] = [];
  /**
   * Bundles read for this draw (a receipt's context) and the reads in flight — one read per bundle per refresh, whoever asks.
   * Never kept across refreshes: the bank settles a due hold on any read, so a bundle read earlier can be stale.
   */
  readonly #bundles = new Map<string, BundleRead>();
  readonly #bundleReads = new Map<string, Promise<BundleRead>>();
  #seq = 0;
  #rangeSeq = 0;
  #inflight = false;
  /** Where focus goes back to, kept until the draw a refresh in flight is about to make. */
  #focusTo: { readonly data: "open" | "mandate"; readonly value: string } | undefined;
  #opener: string | undefined;
  #sheet: SheetHandle | undefined;
  #sheetSeq = 0;
  #opening = false;

  constructor(deps: InvestDeps) {
    this.#d = deps;
    this.#receipts = new ReceiptSheets({
      api: deps.api,
      host: deps.host,
      reduced: deps.reduced,
      judgeOn: deps.judgeOn,
      onProof: deps.onProof,
      behind: () => this.view.parts,
    });
    this.view = new InvestView({
      onTab: deps.onTab,
      assistant: deps.assistant,
      onRetry: () => void this.refresh(),
      onRetryChart: () => void this.#rangeTo(this.#range, true),
      onRange: (r) => void this.#rangeTo(r, false),
      onOpenHolding: (id) => this.#openHolding(id),
      onOpenReceipt: (key) => void this.#openReceipt(key),
      onOpenMandate: (id) => {
        this.#opener = id;
        deps.onSchedule(id);
      },
      onSeeAllOrders: () => deps.onSeeAllOrders(),
      onUnlock: () => deps.onUnlock(),
      onUpdateKyc: () => deps.onUpdateKyc(),
    });
  }

  /** DCTA-231 V2: the tab has been drawn from the bank's reads (a change at the bank reads it again; never drawn: nothing to do). */
  get drawn(): boolean {
    return this.#reads !== undefined;
  }

  /** The tab is on screen: its last draw is still there (the same nodes); fresh reads replace it. */
  show(): void {
    void this.refresh();
  }

  /** Back from a layer the tab opened (a schedule): focus on the row that opened it (WCAG 2.4.3). */
  returnFocus(): void {
    if (this.#opener !== undefined) this.#returnTo("mandate", this.#opener);
    this.#opener = undefined;
  }

  async refresh(): Promise<void> {
    const seq = ++this.#seq;
    // A refresh draws its own range: an older range answer still in flight is dropped.
    this.#rangeSeq += 1;
    this.#inflight = true;
    const range = this.#range;
    this.#bundles.clear();
    this.#bundleReads.clear();
    if (this.#reads === undefined) this.view.loading();
    const api = this.#d.api;
    try {
      const [valuation, portfolio] = await Promise.all([api.valuation(), api.portfolio()]);
      const settlement = portfolio.settlement_account;
      const held = (valuation.holdings ?? []).length > 0;
      let reads: BankReads;
      if (settlement === undefined && !held) {
        // No trading account and nothing held: the two lines, and nothing else to read.
        reads = { valuation, settlement, pending: [], mandates: [], orders: undefined, history: { kind: "absent" }, range, locked: false };
      } else {
        const [pending, mandates, accounts, history, orders] = await Promise.all([
          api.pending().then((p) => p.pending, rethrowAuth),
          settlement === undefined
            ? Promise.resolve([] as readonly MandateView[])
            : api.mandates({ destination_id: settlement.account_id, status: "active" }).then((m) => m.mandates, rethrowAuth),
          api.accounts().then((a) => a.accounts, rethrowAuth),
          held ? this.#history(range) : Promise.resolve<HistoryState>({ kind: "absent" }),
          this.#orders(settlement),
        ]);
        this.#accounts = accounts ?? (settlement === undefined ? [] : [settlement]);
        reads = { valuation, settlement, pending, mandates, orders, history, range, locked: this.#accounts.some((a) => a.locked) };
      }
      if (seq !== this.#seq) return;
      // A range pressed while this refresh was in flight has drawn its own answer: the refresh keeps it, never the older one.
      const latest = this.#reads;
      if (range !== this.#range && latest?.range === this.#range) reads = { ...reads, range: latest.range, history: latest.history };
      this.#reads = reads;
      this.view.show(this.#model(reads));
      if (this.#focusTo !== undefined) this.view.row(this.#focusTo.data, this.#focusTo.value)?.focus({ preventScroll: true });
    } catch (error) {
      if (seq !== this.#seq) return;
      if (auth(error)) {
        this.#d.onSignedOut();
        return;
      }
      this.view.unreachable();
    } finally {
      if (seq === this.#seq) {
        this.#inflight = false;
        this.#focusTo = undefined;
      }
    }
  }

  #model(reads: BankReads): ReturnType<typeof investModel> {
    return investModel({ ...reads, lapsed: this.#d.lapsed(), nowMs: this.#d.now() });
  }

  /** Read A for a range. */
  #history(range: HistoryRange): Promise<HistoryState> {
    return historyState(this.#d.api.portfolioHistory(range));
  }

  /** Read C, or (§4.3 rung 3, and when C fails) the trading account's securities lines with their bundles and receipts. */
  async #orders(settlement: AccountView | undefined): Promise<OrdersRead | undefined> {
    const api = this.#d.api;
    if (!this.#ordersAbsent) {
      try {
        const route = await api.portfolioOrders(3);
        if (route !== undefined && Array.isArray(route.orders)) return { kind: "route", orders: route.orders };
        this.#ordersAbsent = route === undefined;
      } catch (error) {
        if (auth(error)) throw error;
      }
    }
    // No trading account: no ledger to read either — so no "No orders yet." that nothing was read to say.
    if (settlement === undefined) return undefined;
    try {
      const { entries } = await api.ledgerEntries(20, settlement.account_id);
      this.#entries = entries;
      const lines = await Promise.all(
        filledLines(entries).map(async (entry): Promise<FilledLine> => {
          const id = entry.bundle_id;
          const bundle = id === undefined ? undefined : await this.#bundle(id).catch(rethrowAuth);
          return bundle === undefined ? { entry } : { entry, bundle };
        }),
      );
      return { kind: "ledger", lines };
    } catch (error) {
      if (auth(error)) throw error;
      return undefined;
    }
  }

  /** A bundle's two reads — once per bundle, whoever asks (two lines of one bundle share them); a failed read is not kept. */
  #bundle(id: string): Promise<BundleRead> {
    const known = this.#bundleReads.get(id);
    if (known !== undefined) return known;
    const read = Promise.all([this.#d.api.bundle(id), this.#d.api.receipt(id)]).then(([view, receipt]) => {
      const b = { view, receipt };
      this.#bundles.set(id, b);
      return b;
    });
    this.#bundleReads.set(id, read);
    read.catch(() => this.#bundleReads.delete(id));
    return read;
  }

  #forget(id: string): void {
    this.#bundles.delete(id);
    this.#bundleReads.delete(id);
  }

  /** A range pressed (or the chart's Retry): the frame holds while the bank answers; a newer press wins. */
  async #rangeTo(range: HistoryRange, retry: boolean): Promise<void> {
    if (this.#reads === undefined) return;
    this.#range = range;
    const seq = ++this.#rangeSeq;
    if (!retry) this.view.chartBusy();
    let history: HistoryState;
    try {
      history = await this.#history(range);
    } catch {
      this.#d.onSignedOut();
      return;
    }
    const reads = this.#reads;
    if (seq !== this.#rangeSeq || reads === undefined) return;
    this.#reads = { ...reads, range, history };
    this.view.showChart(this.#model(this.#reads));
  }

  #context(): ReceiptContext {
    return { bundles: this.#bundles, entries: this.#entries, accounts: this.#accounts, cards: [] };
  }

  /** An order's or a held buy's receipt, over the tab — read fresh each time (a hold the bank has settled is done, not held). */
  async #openReceipt(key: string): Promise<void> {
    if (this.#opening || this.#receipts.isOpen || this.#sheet !== undefined) return;
    this.#opening = true;
    try {
      const sep = key.lastIndexOf(":");
      const bundleId = sep > 0 ? key.slice(0, sep) : undefined;
      if (bundleId !== undefined) {
        this.#forget(bundleId);
        await this.#bundle(bundleId);
      }
      // The read took a while: the tab may be gone, or a sheet up over it — then nothing opens.
      if (!this.view.body.isConnected || this.#sheet !== undefined) return;
      const model = receiptModel(key, this.#context());
      if (model === undefined) return;
      // Its row as drawn now: where focus goes back to when the receipt closes (rows are rebuilt by every draw).
      this.view.row("key", key)?.focus({ preventScroll: true });
      this.#receipts.open({
        model,
        context: () => this.#context(),
        // After a cancel: what the bank now holds (the held buy gone, the cash back), then the receipt as it now reads.
        refresh: async () => {
          if (bundleId !== undefined) this.#forget(bundleId);
          await this.refresh();
        },
        reopen: () => void this.#openReceipt(key),
      });
    } catch (error) {
      if (auth(error)) this.#d.onSignedOut();
      else this.view.unreachable();
    } finally {
      this.#opening = false;
    }
  }

  /** §6.3: the holding's sheet — the bank's surface — with its own chart (read B) and its own range. */
  #openHolding(instrumentId: string): void {
    const v = this.#reads?.valuation;
    if (v === undefined || this.#sheet !== undefined || this.#receipts.isOpen || this.#opening) return;
    const model = holdingSheet(v, instrumentId);
    if (model === undefined) return;
    const load = async (r: HistoryRange): Promise<void> => {
      const mine = ++this.#sheetSeq;
      let state: HistoryState;
      try {
        state = await historyState(this.#d.api.instrumentHistory(instrumentId, r));
      } catch {
        this.#d.onSignedOut();
        return;
      }
      if (mine !== this.#sheetSeq || this.#sheet === undefined) return;
      sheet.setChart(sheetChartOf(state, model, r), r);
    };
    const sheet = holdingSheetView(model, {
      onBuyMore: () => void this.#closeHolding(instrumentId, false).then(() => this.#d.assistant.onAssistant("text")),
      onDone: () => void this.#closeHolding(instrumentId, true),
      onRange: (r) => void load(r),
    });
    this.#sheet = openSheet(this.#d.host, sheet.sheet, {
      behind: this.view.parts,
      reduced: this.#d.reduced(),
      animate: true,
      onDismiss: () => void this.#closeHolding(instrumentId, true),
    });
    void load("1m");
  }

  async #closeHolding(instrumentId: string, focusRow: boolean): Promise<void> {
    const sheet = this.#sheet;
    this.#sheet = undefined;
    this.#sheetSeq += 1;
    await sheet?.close();
    if (focusRow) this.#returnTo("open", instrumentId);
  }

  /** Focus on a row as the tab now draws it; again on the draw a refresh in flight is about to make (rows are rebuilt). */
  #returnTo(data: "open" | "mandate", value: string): void {
    this.view.row(data, value)?.focus({ preventScroll: true });
    this.#focusTo = this.#inflight ? { data, value } : undefined;
  }
}
